import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Asset,
  AssetDetailResponse,
  AssetsListResponse,
  AssetType,
} from '@stfors/shared-types';
import { api } from '../client';

export interface AssetFilters {
  asset_type?: AssetType;
  assigned_to_user_id?: number;
  unassigned?: boolean;
  /** Only assets no live time entry sells or reserves — the Log Time drive picker (0058). */
  available?: boolean;
  /** The Assets page tabs (0060). Omitted = every asset, as the expense pickers want. */
  status?: AssetStatusFilter;
}

export type AssetStatusFilter = 'in_inventory' | 'sold' | 'disposed' | 'all';

export function useAssets(filters: AssetFilters = {}, opts: { enabled?: boolean } = {}) {
  const params = new URLSearchParams();
  if (filters.asset_type) params.set('asset_type', filters.asset_type);
  if (filters.assigned_to_user_id !== undefined) {
    params.set('assigned_to_user_id', String(filters.assigned_to_user_id));
  }
  if (filters.unassigned) params.set('unassigned', 'true');
  if (filters.available) params.set('available', 'true');
  if (filters.status) params.set('status', filters.status);
  const qs = params.toString();
  const url = qs.length > 0 ? `/api/assets?${qs}` : '/api/assets';
  return useQuery({
    queryKey: ['assets', { ...filters }],
    queryFn: ({ signal }) => api.get<AssetsListResponse>(url, signal),
    enabled: opts.enabled ?? true,
  });
}

export function useAssetDetail(id: number | null) {
  return useQuery({
    queryKey: ['assets', { id }],
    queryFn: ({ signal }) =>
      api.get<AssetDetailResponse>(`/api/assets/${id}`, signal),
    enabled: id !== null,
  });
}

export interface CreateAssetInput {
  name: string;
  asset_type: AssetType;
  serial_number?: string;
  make_model?: string;
  purchase_date?: number;
  purchase_price?: string;
  warranty_expiry?: number;
  assigned_to_user_id?: number;
  location?: string;
  notes?: string;
}

export interface UpdateAssetInput {
  id: number;
  name?: string;
  asset_type?: AssetType;
  serial_number?: string | null;
  make_model?: string | null;
  purchase_date?: number | null;
  purchase_price?: string | null;
  warranty_expiry?: number | null;
  location?: string | null;
  notes?: string | null;
}

export function useUpdateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateAssetInput) =>
      api.patch<{ asset: Asset }>(`/api/assets/${id}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

export function useCreateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAssetInput) =>
      api.post<{ asset: Asset }>('/api/assets', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

export function useAssignAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, userId }: { id: number; userId: number }) =>
      api.post<{ asset: Asset }>(`/api/assets/${id}/assign`, {
        assigned_to_user_id: userId,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

export function useUnassignAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ asset: Asset }>(`/api/assets/${id}/unassign`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

export function useDeleteAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/assets/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

export interface RetireAssetInput {
  id: number;
  disposition: 'sold' | 'disposed';
  disposed_at: number;
  /** Sold only — what it sold for. */
  disposal_amount?: string;
  disposal_note?: string;
}

/** The asset has left the firm: sold another way, or disposed of (0060). */
export function useRetireAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: RetireAssetInput) =>
      api.post<{ asset: Asset }>(`/api/assets/${id}/retire`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['assets'] });
      void qc.invalidateQueries({ queryKey: ['reports'] }); // equipment sold is P&L income
    },
  });
}

/** Undo a retirement — back to inventory. */
export function useRestoreAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<{ asset: Asset }>(`/api/assets/${id}/restore`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['assets'] });
      void qc.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}
